import { fbt } from 'fbtee'
import type { GuestPromptContext } from '../hooks/useGuestGuard'
import type { GuestIntent } from './guestIntent'

// Reusable upsell-prompt contexts for `useGuestGuard`'s `prompt(...)` and
// `guard(fn, ctx)`. Keep one entry per distinct user intent so the wording is
// identical wherever the same action is offered.

export function createGroupPrompt(): GuestPromptContext {
  return {
    intent: 'create_group',
    title: fbt(
      'Log in to create a group',
      'Title of the upsell dialog when a guest taps the create-group button or empty-state card',
    ),
    description: fbt(
      'Make plans with friends. Compare festival picks and see who else is going.',
      'Body of the upsell dialog when a guest taps create group',
    ),
    afterSignIn: { pathname: '/social', search: '?create=group' },
  }
}

export function rsvpEventPrompt(): GuestPromptContext {
  return {
    intent: 'event_interest',
    title: fbt(
      'Log in to RSVP to events',
      'Title of the upsell dialog when a guest taps the event RSVP / interest button',
    ),
    description: fbt(
      "Log in so your friends can see which events you're interested in and going to.",
      'Body of the upsell dialog when a guest taps the event RSVP button',
    ),
  }
}

export function friendInvitePrompt(name: string): GuestPromptContext {
  return {
    intent: 'add_friend',
    title: fbt(
      'Log in to add ' + fbt.param('name', name),
      'Title of the upsell dialog when a guest opens a friend invite link',
    ),
    description: fbt(
      "Log in to become friends and see each other's festival picks.",
      'Body of the upsell dialog when a guest opens a friend invite link',
    ),
  }
}

// Guest upsell for posting to a set feed — used by both EventPage and
// FestivalModePage's SetPostComposer onRequireAuth callback.
export function postToFeedPrompt(): GuestPromptContext {
  return {
    intent: 'post',
    title: fbt(
      'Log in to post',
      'Guest upsell title for posting to a set feed',
    ),
    description: fbt(
      'Create an account to share comments and recommendations with your crew.',
      'Guest upsell body for posting to a set feed',
    ),
  }
}

// Guest upsell for marking someone a favorite — used from a user profile and
// from festival mode's friend detail view.
export function favoritePrompt(): GuestPromptContext {
  return {
    intent: 'favorite',
    title: fbt(
      'Log in to favorite people',
      'Guest upsell title when a guest taps the favorite star',
    ),
    description: fbt(
      'Log in to keep favorites and highlight them across Faro.',
      'Guest upsell body when a guest taps the favorite star',
    ),
  }
}

// Guest upsell for suggesting an event — used from the browse filter row and
// both the empty and no-search-results states.
export function suggestEventPrompt(): GuestPromptContext {
  return {
    intent: 'suggest_event',
    title: fbt(
      'Log in to suggest an event',
      'Guest upsell title when a guest taps the suggest-an-event button',
    ),
    description: fbt(
      "Tell us what's missing and we'll add it to Faro.",
      'Guest upsell body when a guest taps the suggest-an-event button',
    ),
  }
}

export function requestRegionPrompt(): GuestPromptContext {
  return {
    intent: 'request_region',
    title: fbt(
      'Log in to request your area',
      'Guest upsell title when a guest taps the request-an-area row in the region picker',
    ),
    description: fbt(
      "Tell us where you are and we'll use it to decide which area to add next.",
      'Guest upsell body when a guest taps the request-an-area row',
    ),
  }
}

export function scheduleLeadPrompt(): GuestPromptContext {
  return {
    intent: 'schedule_lead',
    title: fbt(
      'Log in to tell us',
      'Guest upsell title when a guest taps the report-set-times button on an empty schedule',
    ),
    description: fbt(
      "Let us know the set times are out and we'll add them to the schedule.",
      'Guest upsell body when a guest taps the report-set-times button',
    ),
  }
}

// Default copy shared by every interest-style action — hearting an event,
// artist, or set, and toggling going/interested on a group's linked event.
// Used as the fallback inside <GuestSignInDialog> when no explicit ctx is
// passed to `guard(fn)`.
export function interestPrompt(intent: GuestIntent = 'set_interest'): GuestPromptContext {
  return {
    intent,
    title: fbt(
      'Log in to add your picks',
      'Default title for the upsell dialog when a guest tries to mark interest',
    ),
    description: fbt(
      "Log in so your friends can see what you're interested in and going to.",
      'Default body for the upsell dialog explaining the benefit of logging in',
    ),
  }
}

export function crewPrompt(): GuestPromptContext {
  return {
    intent: 'open_crew',
    title: fbt('Log in to see your crew', 'Guest upsell title when a guest taps the crew upsell card on the event page'),
    description: fbt(
      'See who else is going and what your friends are into.',
      'Guest upsell body when a guest taps the crew upsell card on the event page',
    ),
  }
}

// Used from the "Share picks" chip on both the event schedule and festival mode.
export function shareSchedulePrompt(): GuestPromptContext {
  return {
    intent: 'share_schedule',
    title: fbt('Log in to share your picks', 'Guest upsell title when a guest taps the share-picks chip on a schedule'),
    description: fbt(
      'Your picks are saved on this device. Log in to keep them and share them with your crew.',
      'Guest upsell body when a guest taps the share-picks chip on a schedule',
    ),
  }
}

// Guest upsell for the festival-mode add-people sheet. A guest has no friend
// code and can't own a group invite, so the code they'd hold out would be blank.
export function addPeoplePrompt(): GuestPromptContext {
  return {
    intent: 'add_friend',
    title: fbt(
      'Log in to add people',
      'Guest upsell title when a guest taps add people in festival mode',
    ),
    description: fbt(
      'Log in to get your own code so people can join your crew on the spot.',
      'Guest upsell body when a guest taps add people in festival mode',
    ),
  }
}

// Guest upsell for the My requests row on the profile — a guest has nothing to
// list, so the wall names what signing in would let them track.
export function myRequestsPrompt(): GuestPromptContext {
  return {
    intent: 'my_requests',
    title: fbt(
      'Log in to see your requests',
      'Guest upsell title when a guest taps the My requests row on the profile',
    ),
    description: fbt(
      "Track what happened to the events, set times and areas you've asked for.",
      'Guest upsell body when a guest taps the My requests row on the profile',
    ),
    afterSignIn: { pathname: '/requests' },
  }
}

export function searchPeoplePrompt(): GuestPromptContext {
  return {
    intent: 'search_people',
    title: fbt('Log in to find people', 'Guest upsell title when a guest taps the People row in global search'),
    description: fbt(
      'Find friends by name or friend code and add them.',
      'Guest upsell body when a guest taps the People row in global search',
    ),
  }
}

export function hostPregamePrompt(): GuestPromptContext {
  return {
    intent: 'host_pregame',
    title: fbt('Log in to host a pre-game', 'Title of the upsell dialog when a guest taps "Host a pre-game"'),
    description: fbt(
      "Tell your crew where and when you're meeting before the event.",
      'Body of the upsell dialog when a guest taps "Host a pre-game"',
    ),
  }
}

export function eventTicketsPrompt(eventId: string): GuestPromptContext {
  return {
    intent: 'event_tickets',
    title: fbt('Log in to sort out tickets with your crew', 'Title of the upsell dialog when a guest taps a tickets action on a party page'),
    description: fbt(
      'Note who each ticket you bought is for, see who still needs one, and let your crew claim spares. Nothing is bought or sold here.',
      'Body of the upsell dialog when a guest taps a tickets action on a party page',
    ),
    afterSignIn: { pathname: `/events/${eventId}`, search: '' },
  }
}
